"use client";

import { useState, useEffect } from "react";

import { ThreeLevelSelector } from "@/app/indicators/biblio/(components)/ThreeLevelSelector";
import { IndicatorsContext } from "@/app/indicators/(context)/IndicatorsContext";
import { Spinner } from "@nextui-org/react";
import dynamic from "next/dynamic";
import { useRouter } from "next13-progressbar";
import { fetchAreas } from "@/app/indicators/biblio/actions/areas";

// Importar dinámicamente los componentes
const PackedBubble = dynamic(() => import("@/app/charts/PackedBubble"), {
  ssr: false,
  loading: () => <Spinner size="large" label="Cargando gráfico" />,
});

export default function IndicatorsAreasPage() {
  const router = useRouter();
  const [series, setSeries] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [data, setData] = useState([]);

  // Estas variables guardan los valores seleccionados
  const [level1Value, setLevelValue1] = useState(0);
  const [level2Value, setLevelValue2] = useState(-1);
  const [level3Value, setLevelValue3] = useState(-1);

  // Estas variables guardan los valores de los selectores
  const [dataSelector1, setDataSelector1] = useState([]);
  const [dataSelector2, setDataSelector2] = useState([]);
  const [dataSelector3, setDataSelector3] = useState([]);

  const [loading, setLoading] = useState(true);

  function handlePointClick(name) {
    router.push(`/explore/documents?p=1&f=area_str:"${name}"`);
  }

  // Este efecto se ejecuta cuando se actualiza la variable data, que sólo va a pasar una vez
  useEffect(() => {
    setLoading(true);
    fetchAreas().then((resp) => {
      resp = resp.map(({ index, count, label }) => {
        return {
          index: +index,
          label,
          count,
        };
      });
      setData(resp);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!filteredData || filteredData.length === 0) return;

    // 1. Determinamos el nivel
    const indexes = filteredData.map((item) => item.index);
    const minIndex = Math.min(...indexes);
    const maxIndex = Math.max(...indexes);
    const level = maxIndex - minIndex > 10 ? 1 : 2;

    // 2. OPTIMIZACIÓN: Crear un mapa de etiquetas (O(n))
    // Esto evita hacer un .find() dentro de cada iteración del reduce
    const labelMap = new Map(
      filteredData.map((item) => [item.index, item.label]),
    );

    // 3. Agrupación eficiente
    const groupedData = filteredData.reduce((acc, item) => {
      // Calculamos el índice base del grupo
      const groupedNumber =
        level === 1 ? Math.floor(item.index / 10) * 10 : item.index;

      // Buscamos la etiqueta en el mapa.
      // Si no existe (ej. el índice 40 no está en el JSON), usamos un fallback
      const groupedLabel =
        labelMap.get(groupedNumber) || `Otros (Rango ${groupedNumber})`;

      if (!acc[groupedLabel]) {
        acc[groupedLabel] = [];
      }

      acc[groupedLabel].push({
        name: item.label,
        value: item.count,
        area_index: item.index,
      });

      return acc;
    }, {});

    // 4. Transformación final a array de series
    const seriesAux = Object.entries(groupedData).map(([name, data]) => ({
      name,
      data,
    }));

    setSeries(seriesAux);
  }, [filteredData]);

  return (
    <IndicatorsContext.Provider
      value={{
        data,
        filteredData,
        setFilteredData,
        level1Value,
        level2Value,
        level3Value,
        setLevelValue1,
        setLevelValue2,
        setLevelValue3,
        dataSelector1,
        dataSelector2,
        dataSelector3,
        setDataSelector1,
        setDataSelector2,
        setDataSelector3,
        setLoading,
      }}
    >
      <div className="flex flex-col text-left">
        <ThreeLevelSelector />
        {loading ? (
          <Spinner size="large" label="Cargando gráfico" className="mt-5" />
        ) : (
          <div className="flex flex-col md:flex-row gap-5 mt-10">
            <div
              className="w-full md:w-1/3 overflow-auto mb-5 "
              style={{ height: "75vh" }}
            >
              <div className="flex flex-col">
                <div className="flex flex-row border border-solid text-sm uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold py-3">
                  <div className="w-4/5 text-wrap">Área</div>
                  <div className="w-1/5 text-wrap"># pubs</div>
                </div>
              </div>
              <div className="flex flex-col">
                {filteredData.map((area) => (
                  <div
                    className={`flex flex-row border-t-0 border-l-0 border-r-0 whitespace-nowrap py-1 text-tiny ${
                      area.index % 10 === 0 ? "bg-gray-100 font-bold" : ""
                    }`}
                    key={area.index}
                  >
                    <div className="w-4/5 text-wrap text-left">
                      {area.label}
                    </div>
                    <div className="w-1/5 text-wrap">{area.count}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full md:w-2/3">
              <PackedBubble
                series={series}
                handlePointClick={handlePointClick}
              />
            </div>
          </div>
        )}
      </div>
    </IndicatorsContext.Provider>
  );
}
